import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  saasAcceptInvite,
  saasLogin,
  saasLogout,
  saasMe,
  saasSignup,
  SaasError,
  type SaasUser,
} from './saas-client';
import { dropBoot, takeBoot } from './boot';

interface AuthCtx {
  user: SaasUser | null;
  loading: boolean;
  signup: (
    email: string,
    password: string,
    newsletterOptIn?: boolean,
    turnstileToken?: string,
    plan?: string,
  ) => Promise<void>;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  /** Set the password from an invite link; signs in. */
  acceptInvite: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

// Dev-only auth bypass so authed pages can be previewed locally without prod
// Turnstile (local dev proxies /account to the prod API, which rejects any
// captcha token minted on 127.0.0.1). Opt in explicitly:
//   VITE_DEV_AUTH=1 npm run dev
// Gated on import.meta.env.DEV, so it can never be active in a prod build.
const DEV_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === '1';
const DEV_USER: SaasUser = {
  id: 'dev-local',
  email: 'dev@localhost',
  plan_id: 'pro_beta',
  email_verified: true,
  created_at: '2026-01-01T00:00:00Z',
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SaasUser | null>(DEV_AUTH ? DEV_USER : null);
  const [loading, setLoading] = useState(!DEV_AUTH);

  const refresh = useCallback(async () => {
    if (DEV_AUTH) {
      setUser(DEV_USER);
      setLoading(false);
      return;
    }
    try {
      // The first check on a page load reads the /me that index.html started.
      const me = await (takeBoot<SaasUser>('me') ?? saasMe());
      setUser(me);
    } catch (e) {
      // Without a user, the account data index.html fetched alongside is
      // unusable (a 401 without a session); a later sign-in fetches its own.
      dropBoot();
      if (e instanceof SaasError && e.status === 401) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signup = useCallback(
    async (
      email: string,
      password: string,
      newsletterOptIn: boolean = false,
      turnstileToken?: string,
      plan?: string,
    ) => {
      // No session is issued until the user verifies their email, so we don't
      // set the user here — the signup panel shows a "check your email" state.
      await saasSignup(email, password, newsletterOptIn, turnstileToken, plan);
    },
    [],
  );

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string) => {
      const u = await saasLogin(email, password, turnstileToken);
      setUser(u);
    },
    [],
  );

  const acceptInvite = useCallback(async (token: string, password: string) => {
    setUser(await saasAcceptInvite(token, password));
  }, []);

  const logout = useCallback(async () => {
    await saasLogout();
    setUser(null);
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({ user, loading, signup, login, acceptInvite, logout, refresh }),
    [user, loading, signup, login, acceptInvite, logout, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
